import type { ProductCategoryProductCategoryReadModel } from '@r/api/generated/client.schemas.ts';
import {
  buildCategoryLookup,
  categoryHasChildren,
  getCategoryParentId
} from './custom-shop-categories-api.ts';

export type CategoryLinkInvalidReason =
  | 'self_link'
  | 'parent_not_found'
  | 'child_not_found'
  | 'parent_not_root'
  | 'child_already_has_parent'
  | 'child_has_children';

export type CategoryLinkValidation =
  | { isValid: true }
  | { isValid: false; reason: CategoryLinkInvalidReason };

/** Indique si une catégorie a déjà des sous-catégories rattachées. */
function hasLinkedChildren(
  categoryId: number,
  categories: readonly ProductCategoryProductCategoryReadModel[]
): boolean {
  const lookup = buildCategoryLookup(categories);
  return [...lookup.values()].some((category) => getCategoryParentId(category) === categoryId);
}

/** Une catégorie racine peut être parente d'autres catégories. */
export function canBeParentCategory(category: ProductCategoryProductCategoryReadModel): boolean {
  return getCategoryParentId(category) === null;
}

/** Une catégorie racine sans enfants peut devenir sous-catégorie. */
export function canBeChildCategory(
  category: ProductCategoryProductCategoryReadModel,
  categories: readonly ProductCategoryProductCategoryReadModel[] = []
): boolean {
  return (
    getCategoryParentId(category) === null &&
    !categoryHasChildren(category) &&
    !hasLinkedChildren(category.id, categories)
  );
}

/** Valide un lien parent → enfant (un seul niveau). */
export function validateCategoryLink(
  categories: readonly ProductCategoryProductCategoryReadModel[],
  parentId: number,
  childId: number
): CategoryLinkValidation {
  if (parentId === childId) {
    return { isValid: false, reason: 'self_link' };
  }

  const lookup = buildCategoryLookup(categories);
  const parent = lookup.get(parentId);
  const child = lookup.get(childId);

  if (!parent) {
    return { isValid: false, reason: 'parent_not_found' };
  }

  if (!child) {
    return { isValid: false, reason: 'child_not_found' };
  }

  if (!canBeParentCategory(parent)) {
    return { isValid: false, reason: 'parent_not_root' };
  }

  if (getCategoryParentId(child) !== null) {
    return { isValid: false, reason: 'child_already_has_parent' };
  }

  if (categoryHasChildren(child) || hasLinkedChildren(childId, categories)) {
    return { isValid: false, reason: 'child_has_children' };
  }

  return { isValid: true };
}
